import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  TextField,
  Typography,
} from "@mui/material"
import { Download, Eye, Plus, Save, Share2, Trash2, X } from "lucide-react"
import { useCallback, useEffect, useMemo, useState, type JSX } from "react"
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom"

import { useCan } from "@/auth/permissions"
import { BackButton } from "@/components/ui/back-button"
import { ConfirmDialog } from "@/components/ui/confirm-dialog"
import { useToast } from "@/components/ui/use-toast"
import { PdfReader } from "@/components/pdf/pdf-reader"
import { withCustomersAccess } from "@crm-src/components/page-permission-guard"
import { PreShipSectionEditor } from "@crm-src/components/pre-ship/pre-ship-section-editor"
import { PreShipShareDialog } from "@crm-src/components/pre-ship/pre-ship-share-dialog"
import {
  EMPTY_SIGN_OFF,
  invalidValuesOf,
  isStaleError,
  move,
  newSection,
  preShipApi,
  progressOf,
  valueErrors,
  type PreShipQuestionnaire,
  type PreShipSection,
  type PreShipSignOff,
} from "@crm-src/lib/pre-ship-api"

/**
 * One pre-ship questionnaire, as staff edit it (`/crm/pre-ship/:id`).
 *
 * The spreadsheet, on screen (CEO, 2026-10-03): titled sections of lines —
 * Item (with its ⓘ help text) and Value, plus the line's value type
 * (feedback, 2026-10-23). A new questionnaire
 * arrives with the big sections (Basic information filled in from the CRM,
 * Network, Remote access, … empty); staff add lines and sections ("there
 * are always unknowns"). No status, no notes, no templates. The sign-off —
 * completed by, signature, date — is a fixed block at the foot, not a
 * section. The customer fills in the same document in the support portal;
 * its own lines wear a "Customer" chip.
 *
 * Save carries the `lock_version` the page loaded. If the customer saved in
 * the meantime crm-api answers 409 QUESTIONNAIRE_STALE and the page offers a
 * reload instead of overwriting their answers. A value that does not fit its
 * line's type is marked as it is typed and blocks Save; crm-api's own 422
 * INVALID_VALUES marks the lines it names. "Share with customer" hands out a
 * no-login link (PreShipShareDialog). Without `crm:edit` the page is
 * read-only (crm-api refuses the write either way). The PDF — preview and
 * download — is crm-api's XOP-branded rendering, the same file the customer
 * downloads.
 */
function PreShipQuestionnaireDetailPage(): JSX.Element {
  const { id = "" } = useParams()
  const navigate = useNavigate()
  const { toast } = useToast()
  const canEdit = useCan("crm:edit")

  const [record, setRecord] = useState<PreShipQuestionnaire | null>(null)
  const [title, setTitle] = useState("")
  const [sections, setSections] = useState<PreShipSection[]>([])
  const [signOff, setSignOff] = useState<PreShipSignOff>(EMPTY_SIGN_OFF)
  const [dirty, setDirty] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [stale, setStale] = useState(false)
  const [removeSectionIndex, setRemoveSectionIndex] = useState<number | null>(null)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [preview, setPreview] = useState<ArrayBuffer | null>(null)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [shareOpen, setShareOpen] = useState(false)
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({})

  const adopt = useCallback((q: PreShipQuestionnaire) => {
    setRecord(q)
    setTitle(q.title)
    setSections(q.sections)
    setSignOff(q.sign_off ?? EMPTY_SIGN_OFF)
    setDirty(false)
    setStale(false)
    setServerErrors({})
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      adopt(await preShipApi.get(id))
      setLoadError(null)
    } catch (err: any) {
      setLoadError(err?.response?.status === 404 ? "This questionnaire does not exist, or is not one you can see." : err?.response?.data?.error || "The questionnaire could not be loaded.")
    } finally {
      setLoading(false)
    }
  }, [id, adopt])

  useEffect(() => {
    void load()
  }, [load])

  // Leaving with unsaved changes asks first.
  useEffect(() => {
    if (!dirty) return
    const warn = (e: BeforeUnloadEvent) => e.preventDefault()
    window.addEventListener("beforeunload", warn)
    return () => window.removeEventListener("beforeunload", warn)
  }, [dirty])

  const progress = useMemo(() => progressOf(sections), [sections])
  const invalid = useMemo(() => Object.keys(valueErrors(sections)).length, [sections])

  const change = (next: PreShipSection[]) => {
    setSections(next)
    setDirty(true)
    setServerErrors({})
  }

  const save = async () => {
    if (!record || invalid > 0) return
    setSaving(true)
    try {
      adopt(await preShipApi.update(record.id, { lock_version: record.lock_version, title, sections, sign_off: signOff }))
      toast({ title: "Questionnaire saved" })
    } catch (err) {
      const rows = invalidValuesOf(err)
      if (isStaleError(err)) {
        setStale(true)
      } else if (rows) {
        setServerErrors(rows)
        toast({ title: "Not saved", description: (err as any)?.response?.data?.error, variant: "destructive" })
      } else {
        toast({ title: "Not saved", description: (err as any)?.response?.data?.error || "The questionnaire could not be saved.", variant: "destructive" })
      }
    } finally {
      setSaving(false)
    }
  }

  const openPreview = async () => {
    if (!record) return
    setPreviewOpen(true)
    setPreview(null)
    try {
      setPreview(await preShipApi.pdfBytes(record.id))
    } catch {
      setPreviewOpen(false)
      toast({ title: "The PDF could not be rendered", variant: "destructive" })
    }
  }

  const download = async () => {
    if (!record) return
    setDownloading(true)
    try {
      await preShipApi.downloadPdf(record)
    } catch {
      toast({ title: "The PDF could not be downloaded", variant: "destructive" })
    } finally {
      setDownloading(false)
    }
  }

  if (loading && !record) {
    return <LinearProgress />
  }

  if (loadError || !record) {
    return (
      <Box sx={{ display: "grid", gap: 2 }}>
        <BackButton to="/crm/pre-ship" label="Pre-ship Questionnaires" />
        <Alert severity="error">{loadError}</Alert>
      </Box>
    )
  }

  return (
    <Box sx={{ display: "grid", gap: 2, pb: canEdit ? 10 : 2 }} data-cy="preship-detail">
      <BackButton to={`/crm/customers/${record.customer_id}?tab=pre-ship`} label={record.customer_name || "Customer"} />

      <Card>
        <CardContent sx={{ display: "grid", gap: 2 }}>
          <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", flexWrap: "wrap" }}>
            <Box sx={{ flex: 1, minWidth: 280, display: "grid", gap: 1 }}>
              <TextField
                size="small"
                fullWidth
                disabled={!canEdit}
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value)
                  setDirty(true)
                }}
                inputProps={{ "aria-label": "Questionnaire name", "data-cy": "preship-title", maxLength: 200, style: { fontSize: 18, fontWeight: 600 } }}
              />
              <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                <RouterLink to={`/crm/customers/${record.customer_id}`}>{record.customer_name}</RouterLink>
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {canEdit && (
                <Button variant="contained" startIcon={<Share2 size={16} />} onClick={() => setShareOpen(true)} data-cy="preship-share">
                  Share with customer
                </Button>
              )}
              <Button variant="outlined" startIcon={<Eye size={16} />} onClick={() => void openPreview()} data-cy="preship-preview">
                Preview PDF
              </Button>
              <Button variant="outlined" startIcon={<Download size={16} />} onClick={() => void download()} disabled={downloading} data-cy="preship-download">
                Download PDF
              </Button>
              {canEdit && (
                <IconButton aria-label="Delete questionnaire" onClick={() => setDeleteOpen(true)} data-cy="preship-delete">
                  <Trash2 size={18} />
                </IconButton>
              )}
            </Box>
          </Box>

          <Typography sx={{ fontSize: 12, color: "text.secondary" }} data-cy="preship-progress">
            {progress.answered} of {progress.total} lines filled in
          </Typography>

          {record.updated_by_kind === "customer" && (
            <Alert severity="info">
              {record.updated_by_email ? `Last edited by the customer (${record.updated_by_email}).` : "Last edited by the customer, through the shared link."}
            </Alert>
          )}
          {stale && (
            <Alert
              severity="warning"
              data-cy="preship-stale"
              action={
                <Button color="inherit" size="small" onClick={() => void load()}>
                  Reload
                </Button>
              }
            >
              Someone — possibly the customer — saved this questionnaire after you opened it. Reload to see their changes; yours are not saved.
            </Alert>
          )}
        </CardContent>
      </Card>

      {sections.map((section, index) => (
        <PreShipSectionEditor
          key={section.id}
          section={section}
          index={index}
          count={sections.length}
          editable={canEdit}
          onChange={(next) => change(sections.map((s) => (s.id === section.id ? next : s)))}
          onMove={(delta) => change(move(sections, index, delta))}
          onRemove={() => setRemoveSectionIndex(index)}
          serverErrors={serverErrors}
        />
      ))}

      {canEdit && (
        <Box>
          <Button variant="outlined" startIcon={<Plus size={16} />} onClick={() => change([...sections, newSection("New section")])} data-cy="preship-add-section">
            Add section
          </Button>
        </Box>
      )}

      <Card data-cy="preship-sign-off">
        <CardContent sx={{ display: "grid", gap: 1.5 }}>
          <Typography sx={{ fontWeight: 600 }}>Sign-off</Typography>
          <Box sx={{ display: "grid", gap: 1, gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" } }}>
            {(
              [
                ["completed_by", "Completed by", "Print name"],
                ["signature", "Signature", "Sign name"],
                ["signed_on", "Date", "MM/DD/YYYY"],
              ] as const
            ).map(([field, label, placeholder]) => (
              <TextField
                key={field}
                size="small"
                label={label}
                placeholder={placeholder}
                disabled={!canEdit}
                value={signOff[field]}
                onChange={(e) => {
                  setSignOff({ ...signOff, [field]: e.target.value })
                  setDirty(true)
                }}
                inputProps={{ "data-cy": `preship-sign-off-${field}`, maxLength: 200 }}
              />
            ))}
          </Box>
        </CardContent>
      </Card>

      {canEdit && (
        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            zIndex: 2,
            display: "flex",
            alignItems: "center",
            gap: 2,
            justifyContent: "flex-end",
            py: 1.5,
            px: 2,
            bgcolor: "background.paper",
            borderTop: 1,
            borderColor: "divider",
          }}
        >
          <Typography sx={{ mr: "auto", fontSize: 13, color: invalid ? "error.main" : "text.secondary" }} data-cy="preship-save-state">
            {invalid ? `${invalid === 1 ? "One answer needs" : `${invalid} answers need`} fixing before you can save` : dirty ? "Unsaved changes" : "All changes saved"}
          </Typography>
          <Button variant="contained" startIcon={<Save size={16} />} onClick={() => void save()} disabled={!dirty || saving || stale || invalid > 0} data-cy="preship-save">
            {saving ? "Saving..." : "Save"}
          </Button>
        </Box>
      )}

      <ConfirmDialog
        isOpen={removeSectionIndex !== null}
        onClose={() => setRemoveSectionIndex(null)}
        title="Remove this section?"
        description={`"${removeSectionIndex !== null ? sections[removeSectionIndex]?.title || "Untitled section" : ""}" and every answer in it leave the questionnaire when you save.`}
        confirmLabel="Remove section"
        onConfirm={() => {
          if (removeSectionIndex !== null) change(sections.filter((_, i) => i !== removeSectionIndex))
          setRemoveSectionIndex(null)
        }}
      />

      <ConfirmDialog
        isOpen={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        title="Delete this questionnaire?"
        description={`"${record.title}" disappears from the CRM and from ${record.customer_name || "the customer"}'s support portal.`}
        confirmLabel="Delete"
        onConfirm={async () => {
          await preShipApi.remove(record.id)
          toast({ title: "Questionnaire deleted" })
          navigate(`/crm/customers/${record.customer_id}?tab=pre-ship`)
        }}
      />

      {canEdit && (
        <PreShipShareDialog
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          questionnaire={{ ...record, sections }}
          // Only the link changed — never adopt the whole record, which would drop unsaved edits.
          onChange={(next) => setRecord({ ...record, share: next.share ?? null })}
        />
      )}

      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
          <Box sx={{ flex: 1 }}>{record.title}</Box>
          <IconButton aria-label="Close preview" onClick={() => setPreviewOpen(false)}>
            <X size={18} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {preview ? (
            <PdfReader bytes={preview} filename={`${record.title}.pdf`} maxHeight="75vh" containerTestId="preship-pdf-preview" fallbackTestId="preship-pdf-fallback" />
          ) : (
            <LinearProgress />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  )
}

export default withCustomersAccess(PreShipQuestionnaireDetailPage)
